<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import { Head, Link, useForm, usePage } from '@inertiajs/inertia-vue3';
import { Icon } from '@iconify/vue';
import PremiumPageHeader from "@/Components/PremiumPageHeader.vue";
import PremiumTabs from "@/Components/PremiumTabs.vue";
import Buttons from "@/Components/Buttons.vue";
import SelectSearch from "@/Components/SelectSearch.vue";
import axios from 'axios';

const props = defineProps({
    courses: {
        type: Array,
        default: () => []
    },
    students: {
        type: Array,
        default: () => []  // kept for backwards-compat; data loaded via searchStudents API
    },
    stats: {
        type: Object,
        default: () => ({})
    },
    canWrite: {
        type: Boolean,
        default: true
    },
    typeDocuments: {
        type: Array,
        default: () => []
    },
    levels: {
        type: Array,
        default: () => []
    },
    parentescos: {
        type: Array,
        default: () => []
    },
    sedes: {
        type: Array,
        default: () => []
    }
});

const page = usePage();
const isAdminOrStaff = computed(() => page.props.value.auth.user.id_rol <= 3 || (page.props.value.auth.collaborator && page.props.value.auth.collaborator.right && page.props.value.auth.collaborator.right('cursolibre', 'readall')));
const isAdminEnrrole = computed(() => page.props.value.auth.user.id_rol <= 3 || (page.props.value.auth.collaborator && page.props.value.auth.collaborator.right && page.props.value.auth.collaborator.right('cursolibre', 'matricular')));

// Active Tab state
const activeTab = ref(isAdminOrStaff.value ? 'dashboard' : 'courses');

const tabsMenu = computed(() => {
    let tabs = [];
    if (isAdminOrStaff.value) {
        tabs.push({ id: 'dashboard', label: 'Dashboard', icon: 'tabler:layout-dashboard', show: true });
    }
    if (isAdminEnrrole.value) {
        tabs.push({ id: 'students', label: 'Estudiantes', icon: 'tabler:users', show: true });
    }
    tabs.push({ id: 'courses', label: 'Cursos', icon: 'tabler:books', show: true });
    return tabs;
});

// Modals State
const showCourseModal = ref(false);
const showEnrollModal = ref(false);
const showNewStudentModal = ref(false);
const isEditing = ref(false);

// Forms
const courseForm = useForm({
    id: null,
    label: '',
    ref: '',
    label_short: '',
    credits: null,
    anio: new Date().getFullYear(),
    description: '',
    status: true
});

const enrollForm = useForm({
    id_student: '',
    id_course: ''
});

// --- Enroll modal: async student search ---
const enrollStudentSearch = ref('');
const enrollStudentOptions = ref([]);
const enrollStudentLoading = ref(false);
let enrollSearchTimeout = null;

const searchStudentsForEnroll = async (query) => {
    if (!query || query.length < 3) {
        // Fewer than 3 chars: clear options but keep selected if any
        if (!query) enrollStudentOptions.value = [];
        return;
    }
    enrollStudentLoading.value = true;
    try {
        const { data } = await axios.get(route('independent-courses.students'), {
            params: { search: query, page: 1, per_page: 30 }
        });
        enrollStudentOptions.value = data.data || [];
    } catch (e) {
        console.error('Error buscando estudiantes para matrícula', e);
    } finally {
        enrollStudentLoading.value = false;
    }
};

const onEnrollStudentSearch = (query) => {
    enrollStudentSearch.value = query;
    clearTimeout(enrollSearchTimeout);
    if (query.length >= 3) {
        enrollSearchTimeout = setTimeout(() => searchStudentsForEnroll(query), 350);
    } else if (!query) {
        enrollStudentOptions.value = [];
    }
};

// Search filters
const studentSearch = ref('');
const courseSearch = ref('');

// --- Backend-paginated students state ---
const studentsData = ref([]); // current page records
const studentsMeta = ref({ current_page: 1, last_page: 1, total: 0, per_page: 20 });
const studentsLoading = ref(false);

const fetchStudents = async (page = 1) => {
    studentsLoading.value = true;
    try {
        const { data } = await axios.get(route('independent-courses.students'), {
            params: { search: studentSearch.value, page, per_page: studentsMeta.value.per_page }
        });
        studentsData.value = data.data;
        studentsMeta.value = {
            current_page: data.current_page,
            last_page: data.last_page,
            total: data.total,
            per_page: data.per_page
        };
    } catch (e) {
        console.error('Error fetching students', e);
    } finally {
        studentsLoading.value = false;
    }
};

// Debounce search input
let searchTimeout = null;
watch(studentSearch, () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => fetchStudents(1), 350);
});

onMounted(() => fetchStudents(1));

const filteredCourses = computed(() => {
    if (!courseSearch.value) return props.courses;
    const search = courseSearch.value.toLowerCase();
    return props.courses.filter(course => {
        const label = course.label.toLowerCase();
        const ref = course.ref ? course.ref.toLowerCase() : '';
        const desc = course.description ? course.description.toLowerCase() : '';
        return label.includes(search) || ref.includes(search) || desc.includes(search);
    });
});

const refManuallyEdited = ref(false);

const generateRefFromLabel = (label) => {
    if (!label) return '';
    const normalized = label
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase();
    const words = normalized.trim().split(/\s+/);
    const codeWords = words.map(word => {
        const cleanWord = word.replace(/[^A-Z0-9]/g, '');
        return cleanWord.substring(0, 4);
    });
    return `CUR-${codeWords.join('')}`;
};

watch(() => courseForm.label, (newVal) => {
    if (!isEditing.value && !refManuallyEdited.value) {
        courseForm.ref = generateRefFromLabel(newVal);
    }
});

// Course CRUD helpers
const openCreateCourseModal = () => {
    isEditing.value = false;
    refManuallyEdited.value = false;
    courseForm.reset();
    courseForm.id = null;
    showCourseModal.value = true;
};

const openEditCourseModal = (course) => {
    isEditing.value = true;
    refManuallyEdited.value = true;
    courseForm.id = course.id;
    courseForm.label = course.label;
    courseForm.ref = course.ref || '';
    courseForm.label_short = course.label_short || '';
    courseForm.credits = course.credits;
    courseForm.anio = course.anio;
    courseForm.description = course.description || '';
    courseForm.status = !!course.status;
    showCourseModal.value = true;
};

const submitCourseForm = () => {
    if (isEditing.value) {
        courseForm.put(route('independent-courses.update'), {
            onSuccess: () => closeModal()
        });
    } else {
        courseForm.post(route('independent-courses.store'), {
            onSuccess: () => closeModal()
        });
    }
};

const deleteCourse = (courseId) => {
    if (confirm('¿Está seguro de que desea eliminar este curso permanentemente? Las relaciones de matrícula se perderán.')) {
        const deleteForm = useForm({});
        deleteForm.delete(route('independent-courses.destroy', courseId));
    }
};

// New Student Helper
const newStudentForm = useForm({
    // Student
    apellidos: '',
    nombres: '',
    id_type_document: '1',
    num_doc: '',
    domicilio: '',
    sexo: '0',
    fecha_nacimiento: '',
    celular: '',
    id_level: '',
    fecha_matricula: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10),
    sede_origen: '',
    // Contact
    contact_apellidos: '',
    contact_nombres: '',
    contact_id_type_document: '1',
    contact_num_doc: '',
    contact_sexo: '0',
    contact_fecha_nacimiento: '',
    contact_celular: '',
    contact_email: '',
    contact_id_parentesco: '2',
    contact_type: 1 // 1 for responsable de pago by default
});

const openNewStudentModal = () => {
    newStudentForm.reset();
    showNewStudentModal.value = true;
};

const submitNewStudentForm = () => {
    newStudentForm.post(route('student-store-other'), {
        onSuccess: () => {
            showNewStudentModal.value = false;
            newStudentForm.reset();
        }
    });
};

// Enrollment helpers
const openEnrollModal = (studentId = '') => {
    enrollForm.reset();
    enrollForm.id_student = studentId;
    enrollStudentOptions.value = [];
    enrollStudentSearch.value = '';
    // If opening with a pre-selected student, load their data to show in the dropdown
    if (studentId) {
        const found = studentsData.value.find(s => String(s.id) === String(studentId));
        if (found) enrollStudentOptions.value = [found];
    }
    showEnrollModal.value = true;
};

// Auto-fill Contact logic
const isSearchingContact = ref(false);

const searchContact = async () => {
    const doc = newStudentForm.contact_num_doc;
    if (!doc || doc.length < 8) return;
    
    isSearchingContact.value = true;
    try {
        const response = await axios.get(route('get-contact-fordoc'), {
            params: { documento: doc }
        });
        
        if (response.data && response.data.apoderado) {
            const persona = response.data.isotherperson ? response.data.apoderado : response.data.apoderado.person;
            
            if (persona) {
                newStudentForm.contact_apellidos = persona.apellidos || '';
                newStudentForm.contact_nombres = persona.nombres || '';
                if (persona.id_type_document) newStudentForm.contact_id_type_document = persona.id_type_document;
                if (persona.sexo) newStudentForm.contact_sexo = persona.sexo;
                newStudentForm.contact_celular = persona.celular || persona.telefono || '';
                newStudentForm.contact_email = persona.email || '';
            }
        }
    } catch (error) {
        console.error("Error al buscar el contacto:", error);
    } finally {
        isSearchingContact.value = false;
    }
};

const submitEnrollForm = () => {
    enrollForm.post(route('independent-courses.enroll'), {
        onSuccess: () => {
            showEnrollModal.value = false;
            enrollForm.reset();
            fetchStudents(studentsMeta.value.current_page); // refresh current page
        }
    });
};

const unenrollStudent = (studentId, courseId, studentName, courseName) => {
    if (confirm(`¿Retirar al estudiante "${studentName}" del curso "${courseName}"?`)) {
        const unenrollForm = useForm({
            id_student: studentId,
            id_course: courseId
        });
        unenrollForm.post(route('independent-courses.unenroll'), {
            onSuccess: () => fetchStudents(studentsMeta.value.current_page)
        });
    }
};

const toggleCourseStatus = (course) => {
    if (course.status) {
        if (!confirm(`¿Está seguro de que desea desactivar el curso "${course.label}"? Los estudiantes no podrán acceder a sus contenidos ni evaluaciones mientras esté desactivado.`)) {
            return;
        }
    }
    const statusForm = useForm({
        id: course.id,
        label: course.label,
        ref: course.ref,
        label_short: course.label_short,
        credits: course.credits,
        anio: course.anio,
        description: course.description,
        status: !course.status
    });
    statusForm.put(route('independent-courses.update'));
};

const closeModal = () => {
    showCourseModal.value = false;
    showEnrollModal.value = false;
    showNewStudentModal.value = false;
    courseForm.reset();
    enrollForm.reset();
    newStudentForm.reset();
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};
</script>

<template>
    <Head title="Cursos de Formación / Libres" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader 
                title="Cursos de Formación / Libres" 
                subtitle="Módulo de gestión para cursos independientes de niveles escolares o áreas fijas." 
                icon="tabler:certificate" 
                color="indigo"
                :actionsOnBottomOnMobile="false"
            >
                <template #bottom>
                    <PremiumTabs v-model="activeTab" :tabs="tabsMenu" />
                </template>
            </PremiumPageHeader>
        </template>

        <div>
            
            <!-- Messages Banner -->
            <div v-if="$page.props.flash && $page.props.flash.message" 
                 class="mb-6 flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 rounded-xl shadow-sm">
                <Icon icon="tabler:circle-check" class="w-5 h-5 shrink-0" />
                <span class="text-sm font-medium">{{ $page.props.flash.message }}</span>
            </div>

            <!-- TAB 1: DASHBOARD -->
            <div v-if="activeTab === 'dashboard' && isAdminOrStaff" class="space-y-6">
                <!-- Statistic Grid -->
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <!-- Card 1 -->
                    <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-5 flex items-center justify-between shadow-sm hover:shadow transition-all duration-300">
                        <div>
                            <span class="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Cursos Libres</span>
                            <h3 class="text-2xl font-bold text-slate-800 dark:text-white mt-1">{{ stats.total_courses || 0 }}</h3>
                            <p class="text-[10px] text-emerald-500 font-semibold mt-1 flex items-center gap-1">
                                <Icon icon="tabler:circle-check" class="w-3.5 h-3.5" />
                                {{ stats.active_courses || 0 }} activos
                            </p>
                        </div>
                        <div class="p-3.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400">
                            <Icon icon="tabler:book" class="w-6 h-6" />
                        </div>
                    </div>

                    <!-- Card 2 -->
                    <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-5 flex items-center justify-between shadow-sm hover:shadow transition-all duration-300">
                        <div>
                            <span class="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Matrículas Totales</span>
                            <h3 class="text-2xl font-bold text-slate-800 dark:text-white mt-1">{{ stats.total_enrollments || 0 }}</h3>
                            <p class="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                                Registros en base de datos
                            </p>
                        </div>
                        <div class="p-3.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400">
                            <Icon icon="tabler:user-plus" class="w-6 h-6" />
                        </div>
                    </div>

                    <!-- Card 3 -->
                    <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-5 flex items-center justify-between shadow-sm hover:shadow transition-all duration-300">
                        <div>
                            <span class="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Alumnos Inscritos</span>
                            <h3 class="text-2xl font-bold text-slate-800 dark:text-white mt-1">{{ stats.unique_students || 0 }}</h3>
                            <p class="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                                Estudiantes sin duplicados
                            </p>
                        </div>
                        <div class="p-3.5 rounded-xl bg-violet-50 dark:bg-violet-950/30 text-violet-600 dark:text-violet-400">
                            <Icon icon="tabler:school" class="w-6 h-6" />
                        </div>
                    </div>

                    <!-- Card 4 -->
                    <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-5 flex items-center justify-between shadow-sm hover:shadow transition-all duration-300">
                        <div>
                            <span class="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Promedio Alum/Curso</span>
                            <h3 class="text-2xl font-bold text-slate-800 dark:text-white mt-1">
                                {{ stats.total_courses ? (stats.total_enrollments / stats.total_courses).toFixed(1) : '0.0' }}
                            </h3>
                            <p class="text-[10px] text-indigo-500 font-semibold mt-1">
                                Promedio de distribución
                            </p>
                        </div>
                        <div class="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400">
                            <Icon icon="tabler:chart-bar" class="w-6 h-6" />
                        </div>
                    </div>
                </div>

                <!-- Graphs & Detailed stats -->
                <div class="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    <!-- Left: Popular courses -->
                    <div class="lg:col-span-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-6 shadow-sm flex flex-col justify-between">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <Icon icon="tabler:award" class="text-indigo-500 w-5 h-5" />
                                Cursos con Mayor Demanda
                            </h3>
                            <p class="text-xs text-slate-400 mt-0.5">Top 5 de cursos independientes por cantidad de matrículas.</p>
                            
                            <div class="mt-6 space-y-5">
                                <div v-for="(course, index) in stats.popular_courses" :key="course.id" class="flex flex-col gap-1.5">
                                    <div class="flex items-center justify-between text-xs font-medium">
                                        <span class="text-slate-700 dark:text-slate-200 flex items-center gap-2">
                                            <span class="w-5 h-5 flex items-center justify-center rounded bg-slate-100 dark:bg-slate-700 text-[10px] font-bold text-slate-500">{{ index + 1 }}</span>
                                            {{ course.label }}
                                            <span class="text-[10px] text-slate-400">({{ course.ref || 'SIN CÓDIGO' }})</span>
                                        </span>
                                        <span class="text-slate-500 dark:text-slate-400 font-semibold">{{ course.students_count }} Alumnos</span>
                                    </div>
                                    <div class="w-full bg-slate-100 dark:bg-slate-700/50 rounded-full h-2 overflow-hidden">
                                        <div class="bg-indigo-600 dark:bg-indigo-500 h-full rounded-full transition-all duration-500" 
                                             :style="{ width: `${stats.total_enrollments ? (course.students_count / stats.total_enrollments) * 100 : 0}%` }">
                                        </div>
                                    </div>
                                </div>

                                <div v-if="!stats.popular_courses || !stats.popular_courses.length" class="text-center py-10">
                                    <Icon icon="tabler:books-off" class="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto" />
                                    <p class="text-sm text-slate-400 mt-2">No hay matrículas registradas para mostrar popularidad.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Right: Recent Enrollments -->
                    <div class="lg:col-span-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-6 shadow-sm flex flex-col justify-between">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <Icon icon="tabler:history" class="text-violet-500 w-5 h-5" />
                                Matrículas Recientes
                            </h3>
                            <p class="text-xs text-slate-400 mt-0.5">Últimas inscripciones realizadas en la plataforma.</p>

                            <div class="mt-6 space-y-4">
                                <div v-for="enrollment in stats.recent_enrollments" :key="enrollment.created_at" 
                                     class="flex items-start gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800">
                                    <div class="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400">
                                        <Icon icon="tabler:user" class="w-4 h-4" />
                                    </div>
                                    <div class="flex-1 min-w-0">
                                        <h4 class="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">
                                            {{ enrollment.apellidos }}, {{ enrollment.nombres }}
                                        </h4>
                                        <p class="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 flex items-center gap-1">
                                            <Icon icon="tabler:book" class="w-3.5 h-3.5" />
                                            Enrolado en: <span class="text-slate-600 dark:text-slate-300 font-medium truncate max-w-[150px] inline-block">{{ enrollment.course_name }}</span>
                                        </p>
                                    </div>
                                    <span class="text-[9px] text-slate-400 font-medium whitespace-nowrap self-center">
                                        {{ formatDate(enrollment.created_at) }}
                                    </span>
                                </div>

                                <div v-if="!stats.recent_enrollments || !stats.recent_enrollments.length" class="text-center py-10">
                                    <Icon icon="tabler:user-x" class="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto" />
                                    <p class="text-sm text-slate-400 mt-2">Ningún alumno matriculado recientemente.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- TAB 2: ESTUDIANTES & MATRÍCULAS -->
            <div v-if="activeTab === 'students' && isAdminEnrrole" class="space-y-6">
                <!-- Toolbar -->
                <div class="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 p-4 rounded-2xl shadow-sm">
                    <div class="relative w-full sm:max-w-md">
                        <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
                            <Icon icon="tabler:search" class="w-4 h-4" />
                        </span>
                        <input type="text" 
                               v-model="studentSearch"
                               placeholder="Buscar alumno por nombre, apellido, DNI o código..."
                               class="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                    </div>

                    <button v-if="canWrite"
                            @click="openEnrollModal()"
                            class="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs md:text-sm font-semibold transition-all duration-200 shadow-md hover:shadow-lg shadow-indigo-600/10">
                        <Icon icon="tabler:user-plus" class="w-4 h-4" />
                        Matricular Estudiante
                    </button>
                </div>

                <!-- Table/Card List of Students -->
                <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl overflow-hidden shadow-sm">
                    <!-- Loading overlay -->
                    <div v-if="studentsLoading" class="flex items-center justify-center py-16 gap-3 text-slate-400">
                        <svg class="w-5 h-5 animate-spin text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"/>
                        </svg>
                        <span class="text-sm">Cargando estudiantes...</span>
                    </div>
                    <div v-if="!studentsLoading" class="overflow-x-auto">
                        <table class="w-full text-left border-collapse">
                            <thead>
                                <tr class="bg-slate-50/50 dark:bg-slate-900/40 text-xs font-semibold text-slate-400 uppercase border-b border-slate-100 dark:border-slate-700/80">
                                    <th class="py-4 px-6">Código / Estudiante</th>
                                    <th class="py-4 px-6">Nro. Documento</th>
                                    <th class="py-4 px-6">Cursos Matriculados</th>
                                    <th class="py-4 px-6 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-slate-100 dark:divide-slate-700/80 text-sm">
                                <tr v-for="student in studentsData" :key="student.id" class="hover:bg-slate-50/30 dark:hover:bg-slate-700/20 transition-colors">
                                    <td class="py-4 px-6">
                                        <div class="flex items-center gap-3">
                                            <div class="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 font-bold flex items-center justify-center text-xs">
                                                {{ student.nombres.charAt(0) }}{{ student.apellidos.charAt(0) }}
                                            </div>
                                            <div>
                                                <h4 class="font-bold text-slate-800 dark:text-white">{{ student.apellidos }}, {{ student.nombres }}</h4>
                                                <span class="text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded font-mono mt-1 inline-block">
                                                    {{ student.ref || 'SIN REF' }}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="py-4 px-6 text-slate-600 dark:text-slate-300 font-mono">{{ student.num_doc || '—' }}</td>
                                    <td class="py-4 px-6">
                                        <div class="flex flex-wrap gap-2 max-w-lg">
                                            <span v-for="course in student.courses" :key="course.id" 
                                                  class="group/tag inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900 rounded-lg text-xs font-medium">
                                                <Icon icon="tabler:book" class="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                                                {{ course.label }}
                                                <button v-if="canWrite"
                                                        @click="unenrollStudent(student.id, course.id, `${student.nombres} ${student.apellidos}`, course.label)"
                                                        class="opacity-60 hover:opacity-100 text-slate-400 hover:text-rose-500 ml-1 transition-all rounded p-0.5 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                                                        title="Cancelar matrícula">
                                                    <Icon icon="tabler:x" class="w-3 h-3" />
                                                </button>
                                            </span>
                                            <span v-if="!student.courses || !student.courses.length" class="text-xs text-slate-400 italic">
                                                Ningún curso de formación asignado
                                            </span>
                                        </div>
                                    </td>
                                    <td class="py-4 px-6 text-right">
                                        <button v-if="canWrite"
                                                @click="openEnrollModal(student.id)"
                                                class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:text-white dark:text-indigo-400 dark:hover:text-white hover:bg-indigo-600 border border-indigo-200 dark:border-indigo-800 hover:border-indigo-600 rounded-lg transition-all duration-200">
                                            <Icon icon="tabler:plus" class="w-3.5 h-3.5" />
                                            Asociar
                                        </button>
                                    </td>
                                </tr>
                                <tr v-if="!studentsLoading && !studentsData.length">
                                    <td colspan="4" class="py-10 text-center text-slate-400 italic">
                                        No se encontraron alumnos con los criterios de búsqueda.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="!studentsLoading && studentsMeta.last_page > 1"
                         class="flex items-center justify-between border-t border-slate-100 dark:border-slate-700/80 px-6 py-3 text-sm text-slate-500 dark:text-slate-400">
                        <span>Mostrando {{ studentsData.length }} de {{ studentsMeta.total }} estudiantes</span>
                        <div class="flex items-center gap-1">
                            <button @click="fetchStudents(studentsMeta.current_page - 1)"
                                    :disabled="studentsMeta.current_page === 1"
                                    class="px-2 py-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:pointer-events-none transition-colors">
                                <Icon icon="tabler:chevron-left" class="w-4 h-4" />
                            </button>
                            <template v-for="p in studentsMeta.last_page" :key="p">
                                <button v-if="p === 1 || p === studentsMeta.last_page || Math.abs(p - studentsMeta.current_page) <= 1"
                                        @click="fetchStudents(p)"
                                        :class="[
                                            'px-3 py-1 rounded-lg text-xs font-medium transition-colors',
                                            p === studentsMeta.current_page
                                                ? 'bg-indigo-600 text-white'
                                                : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                                        ]">
                                    {{ p }}
                                </button>
                                <span v-else-if="p === 2 || p === studentsMeta.last_page - 1" class="px-1">…</span>
                            </template>
                            <button @click="fetchStudents(studentsMeta.current_page + 1)"
                                    :disabled="studentsMeta.current_page === studentsMeta.last_page"
                                    class="px-2 py-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:pointer-events-none transition-colors">
                                <Icon icon="tabler:chevron-right" class="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <!-- Total when single page -->
                    <div v-else-if="!studentsLoading && studentsData.length"
                         class="px-6 py-3 border-t border-slate-100 dark:border-slate-700/80 text-xs text-slate-400">
                        Total: {{ studentsMeta.total }} estudiante{{ studentsMeta.total !== 1 ? 's' : '' }}
                    </div>
                </div>
            </div>

            <!-- TAB 3: CURSOS MANAGEMENT (CRUD) -->
            <div v-if="activeTab === 'courses'" class="space-y-6">
                <!-- Toolbar -->
                <div class="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 p-4 rounded-2xl shadow-sm">
                    <div class="relative w-full sm:max-w-md">
                        <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
                            <Icon icon="tabler:search" class="w-4 h-4" />
                        </span>
                        <input type="text" 
                               v-model="courseSearch"
                               placeholder="Buscar curso libre por nombre, código..."
                               class="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                    </div>

                    <button v-if="$page.props.auth.user.id_rol <= 3 || ($page.props.auth.collaborator && $page.props.auth.collaborator.right && $page.props.auth.collaborator.right('cursolibre', 'write'))"
                            @click="openCreateCourseModal"
                            class="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs md:text-sm font-semibold transition-all duration-200 shadow-md hover:shadow-lg shadow-indigo-600/10">
                        <Icon icon="tabler:plus" class="w-4 h-4" />
                        Nuevo Curso de Formación
                    </button>
                </div>

                <!-- Courses Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div v-for="course in filteredCourses" :key="course.id" 
                         class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col overflow-hidden">
                        <div class="p-6 flex-1 flex flex-col justify-between">
                            <div>
                                <div class="flex items-start justify-between gap-3">
                                    <div>
                                        <span class="text-[10px] font-bold font-mono px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 rounded">
                                            {{ course.ref || 'SIN CÓDIGO' }}
                                        </span>
                                        <h4 class="font-bold text-slate-800 dark:text-white text-base mt-2 line-clamp-2">
                                            {{ course.label }}
                                        </h4>
                                    </div>
                                    <div class="shrink-0">
                                        <!-- Active/Inactive Switch Toggle -->
                                        <div v-if="$page.props.auth.user.id_rol <= 3 || ($page.props.auth.collaborator && $page.props.auth.collaborator.right && $page.props.auth.collaborator.right('cursolibre', 'write'))">
                                            <button v-if="canWrite"
                                                    @click="toggleCourseStatus(course)"
                                                    :class="[course.status ? 'bg-indigo-600 dark:bg-indigo-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500']"
                                                    class="px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase transition-all duration-200 flex items-center gap-1 shadow-sm">
                                                <Icon :icon="course.status ? 'tabler:eye' : 'tabler:eye-off'" class="w-3.5 h-3.5" />
                                                {{ course.status ? 'Activo' : 'Baja' }}
                                            </button>
                                            <div v-else
                                                :class="[course.status ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300' : 'bg-slate-100 dark:bg-slate-800 text-slate-500']"
                                                class="px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase flex items-center gap-1">
                                                <Icon :icon="course.status ? 'tabler:eye' : 'tabler:eye-off'" class="w-3.5 h-3.5" />
                                                {{ course.status ? 'Activo' : 'Baja' }}
                                            </div>
                                        </div>
                                        <div v-else
                                            :class="[course.status ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300' : 'bg-slate-100 dark:bg-slate-800 text-slate-500']"
                                            class="px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase flex items-center gap-1">
                                            <Icon :icon="course.status ? 'tabler:eye' : 'tabler:eye-off'" class="w-3.5 h-3.5" />
                                            {{ course.status ? 'Activo' : 'Baja' }}
                                        </div>
                                    </div>
                                </div>

                                <p class="text-xs text-slate-400 mt-2 line-clamp-3">
                                    {{ course.description || 'Sin descripción disponible para este curso.' }}
                                </p>
                            </div>

                            <!-- Course Metrics footer -->
                            <div class="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between text-xs">
                                <div class="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                                    <Icon icon="tabler:calendar" class="w-4 h-4 text-slate-400" />
                                    Año: <span class="font-semibold text-slate-700 dark:text-slate-200">{{ course.anio }}</span>
                                </div>
                                <div v-if="course.credits" class="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                                    <Icon icon="tabler:award" class="w-4 h-4 text-slate-400" />
                                    Créditos: <span class="font-semibold text-slate-700 dark:text-slate-200">{{ course.credits }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Card Actions Footer -->
                        <div class="bg-slate-50/50 dark:bg-slate-900/20 px-6 py-3 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between gap-3">
                            <Link :href="route('independent-courses.show', course.id)"
                                  class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/30 dark:hover:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-lg text-xs font-semibold transition-all border border-indigo-100 dark:border-indigo-800/40"
                                  title="Ingresar al curso">
                                <Icon icon="tabler:login" class="w-4 h-4" />
                                <span>Ingresar</span>
                            </Link>
                            <div v-if="$page.props.auth.user.id_rol <= 3 || ($page.props.auth.collaborator && $page.props.auth.collaborator.right && $page.props.auth.collaborator.right('cursolibre', 'write'))" class="flex items-center gap-2">
                                <button @click="openEditCourseModal(course)"
                                        class="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 rounded-lg transition-all"
                                        title="Editar curso">
                                    <Icon icon="tabler:edit" class="w-4 h-4" />
                                </button>
                                <button @click="toggleCourseStatus(course)"
                                        :class="[course.status ? 'text-slate-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30' : 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30']"
                                        class="p-2 rounded-lg transition-all"
                                        :title="course.status ? 'Desactivar curso' : 'Activar curso'">
                                    <Icon :icon="course.status ? 'tabler:circle-minus' : 'tabler:circle-check'" class="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-if="!filteredCourses.length" class="col-span-full text-center py-12 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl">
                        <Icon icon="tabler:books-off" class="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto" />
                        <h4 class="text-sm font-semibold text-slate-800 dark:text-white mt-3">No hay cursos creados o asignados</h4>
                        <p class="text-xs text-slate-400 mt-1">Crea un curso libre para comenzar a matricular alumnos.</p>
                        <button v-if="canWrite"
                                @click="openCreateCourseModal"
                                class="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold transition-all">
                            <Icon icon="tabler:plus" class="w-3.5 h-3.5" />
                            Agregar Curso
                        </button>
                    </div>
                </div>
            </div>

            <!-- MODAL: CREAR / EDITAR CURSO -->
            <div v-if="showCourseModal" class="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
                <div class="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-sm transition-opacity" @click="closeModal"></div>
                <div class="relative w-full max-w-lg mx-auto my-6 z-10 px-4">
                    <div class="relative flex flex-col w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl shadow-xl outline-none focus:outline-none transition-all">
                        
                        <!-- Header -->
                        <div class="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-700/80">
                            <h3 class="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <Icon icon="tabler:book" class="text-indigo-600 w-5 h-5" />
                                {{ isEditing ? 'Editar Curso Especial' : 'Crear Nuevo Curso Especial' }}
                            </h3>
                            <button @click="closeModal" class="p-1 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                                <Icon icon="tabler:x" class="w-5 h-5" />
                            </button>
                        </div>

                        <!-- Form -->
                        <form @submit.prevent="submitCourseForm">
                            <div class="p-6 space-y-4">
                                <div class="grid grid-cols-3 gap-4">
                                    <!-- Código/Ref -->
                                    <div class="col-span-1">
                                        <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Código / Ref</label>
                                        <input type="text" 
                                               v-model="courseForm.ref"
                                               @input="refManuallyEdited = true"
                                               placeholder="CUR-101"
                                               class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                        <span v-if="courseForm.errors.ref" class="text-[10px] text-rose-500 mt-1 block">{{ courseForm.errors.ref }}</span>
                                    </div>
                                    <!-- Nombre -->
                                    <div class="col-span-2">
                                        <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Nombre del Curso *</label>
                                        <input type="text" 
                                               v-model="courseForm.label"
                                               placeholder="Ej. Taller de Robótica"
                                               required
                                               class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                        <span v-if="courseForm.errors.label" class="text-[10px] text-rose-500 mt-1 block">{{ courseForm.errors.label }}</span>
                                    </div>
                                </div>

                                <div class="grid grid-cols-3 gap-4">
                                    <!-- Nombre Corto -->
                                    <div class="col-span-2">
                                        <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Nombre Corto</label>
                                        <input type="text" 
                                               v-model="courseForm.label_short"
                                               placeholder="Ej. Robótica"
                                               class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                        <span v-if="courseForm.errors.label_short" class="text-[10px] text-rose-500 mt-1 block">{{ courseForm.errors.label_short }}</span>
                                    </div>
                                    <!-- Créditos -->
                                    <div class="col-span-1">
                                        <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Créditos</label>
                                        <input type="number" 
                                               v-model="courseForm.credits"
                                               placeholder="0"
                                               class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                        <span v-if="courseForm.errors.credits" class="text-[10px] text-rose-500 mt-1 block">{{ courseForm.errors.credits }}</span>
                                    </div>
                                </div>

                                <div class="grid grid-cols-2 gap-4">
                                    <!-- Año lectivo -->
                                    <div>
                                        <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Año Lectivo *</label>
                                        <input type="number" 
                                               v-model="courseForm.anio"
                                               required
                                               class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                        <span v-if="courseForm.errors.anio" class="text-[10px] text-rose-500 mt-1 block">{{ courseForm.errors.anio }}</span>
                                    </div>
                                    <!-- Estado Switch (solo al editar) -->
                                    <div v-if="isEditing">
                                        <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Estado</label>
                                        <select v-model="courseForm.status"
                                                class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all">
                                            <option :value="true">Activo</option>
                                            <option :value="false">Baja (Inactivo)</option>
                                        </select>
                                    </div>
                                </div>

                                <!-- Descripción -->
                                <div>
                                    <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Descripción</label>
                                    <textarea v-model="courseForm.description"
                                              placeholder="Escribe detalles del curso..."
                                              rows="3"
                                              class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all"></textarea>
                                    <span v-if="courseForm.errors.description" class="text-[10px] text-rose-500 mt-1 block">{{ courseForm.errors.description }}</span>
                                </div>
                            </div>

                            <!-- Footer Actions -->
                            <div class="bg-slate-50 dark:bg-slate-900/40 px-6 py-4 border-t border-slate-100 dark:border-slate-700/80 flex items-center justify-end gap-3 rounded-b-2xl">
                                <button type="button" 
                                        @click="closeModal" 
                                        class="px-4 py-2 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                                    Cancelar
                                </button>
                                <button type="submit" 
                                        :disabled="courseForm.processing"
                                        class="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow transition-all flex items-center gap-2">
                                    <Icon v-if="courseForm.processing" icon="tabler:loader" class="w-4 h-4 animate-spin" />
                                    {{ isEditing ? 'Guardar Cambios' : 'Crear Curso' }}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <!-- MODAL: MATRICULAR ESTUDIANTE -->
            <div v-if="showEnrollModal" class="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
                <div class="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-sm transition-opacity" @click="closeModal"></div>
                <div class="relative w-full max-w-md mx-auto my-6 z-10 px-4">
                    <div class="relative flex flex-col w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl shadow-xl outline-none focus:outline-none transition-all">
                        
                        <!-- Header -->
                        <div class="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-700/80">
                            <h3 class="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <Icon icon="tabler:user-plus" class="text-indigo-600 w-5 h-5" />
                                Matricular Alumno
                            </h3>
                            <button @click="closeModal" class="p-1 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                                <Icon icon="tabler:x" class="w-5 h-5" />
                            </button>
                        </div>

                        <!-- Form -->
                        <form @submit.prevent="submitEnrollForm">
                            <div class="p-6 space-y-4">
                                <!-- Estudiante -->
                                <div>
                                    <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Estudiante *</label>
                                    <SelectSearch
                                        class="w-full min-w-[150px]"
                                        v-model="enrollForm.id_student"
                                        :options="enrollStudentOptions"
                                        :get-option-label="(option) => `${option.apellidos}, ${option.nombres} (${option.num_doc ? option.num_doc : 'SIN DOC'})`"
                                        :placeholder="enrollStudentLoading ? 'Buscando...' : 'Escribe para buscar'"
                                        actionBtnLabel="Registrar nuevo estudiante"
                                        :remote-search="true"
                                        @actionBtnClick="openNewStudentModal"
                                        @search="onEnrollStudentSearch"
                                    />
                                    <!-- Hint debajo -->
                                    <p v-if="!enrollStudentLoading && enrollStudentSearch.length > 0 && enrollStudentSearch.length < 3" class="text-[10px] text-amber-500 mt-1">
                                        Escribe {{ 3 - enrollStudentSearch.length }} carácter(es) más para buscar
                                    </p>
                                    <p v-if="enrollStudentLoading" class="text-[10px] text-indigo-500 mt-1 flex items-center gap-1">
                                        <Icon icon="tabler:loader" class="w-3 h-3 animate-spin" /> Buscando estudiantes...
                                    </p>
                                    <span v-if="enrollForm.errors.id_student" class="text-[10px] text-rose-500 mt-1 block">{{ enrollForm.errors.id_student }}</span>
                                </div>

                                <!-- Curso Libre -->
                                <div>
                                    <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Curso Libre/Especial *</label>
                                    <select v-model="enrollForm.id_course"
                                            required
                                            class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all">
                                        <option value="" disabled>Selecciona un curso libre</option>
                                        <option v-for="course in courses.filter(c => c.status === 1)" :key="course.id" :value="course.id">
                                            {{ course.label }} ({{ course.ref || 'SIN CÓDIGO' }})
                                        </option>
                                    </select>
                                    <span v-if="enrollForm.errors.id_course" class="text-[10px] text-rose-500 mt-1 block">{{ enrollForm.errors.id_course }}</span>
                                </div>
                            </div>

                            <!-- Footer Actions -->
                            <div class="bg-slate-50 dark:bg-slate-900/40 px-6 py-4 border-t border-slate-100 dark:border-slate-700/80 flex items-center justify-end gap-3 rounded-b-2xl">
                                <button type="button" 
                                        @click="closeModal" 
                                        class="px-4 py-2 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                                    Cancelar
                                </button>
                                <button type="submit" 
                                        :disabled="enrollForm.processing"
                                        class="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow transition-all flex items-center gap-2">
                                    <Icon v-if="enrollForm.processing" icon="tabler:loader" class="w-4 h-4 animate-spin" />
                                    Matricular
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <!-- MODAL: REGISTRAR NUEVO ESTUDIANTE -->
            <div v-if="showNewStudentModal" class="fixed inset-0 z-[60] flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
                <div class="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-sm transition-opacity" @click="closeModal"></div>
                <div class="relative w-full max-w-4xl mx-auto my-6 z-10 px-4">
                    <div class="relative flex flex-col w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl shadow-xl outline-none focus:outline-none transition-all">
                        
                        <!-- Header -->
                        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-800/50 rounded-t-2xl">
                            <div class="flex items-center gap-3">
                                <div class="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                                    <Icon icon="tabler:user-plus" class="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 class="text-base font-bold text-slate-800 dark:text-white leading-tight">Registrar Estudiante</h3>
                                    <p class="text-xs text-slate-500 dark:text-slate-400">Completa los datos del estudiante y su apoderado</p>
                                </div>
                            </div>
                            <button @click="closeModal" class="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors">
                                <Icon icon="tabler:x" class="w-5 h-5" />
                            </button>
                        </div>

                        <!-- Form Content -->
                        <form @submit.prevent="submitNewStudentForm" class="flex flex-col max-h-[80vh]">
                            <div class="p-6 overflow-y-auto space-y-8">
                                
                                <!-- Sección Estudiante -->
                                <div>
                                    <h4 class="text-sm font-bold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2 border-b border-slate-200 dark:border-slate-700 pb-2">
                                        <Icon icon="tabler:user" class="w-4 h-4 text-indigo-500" />
                                        Datos del Estudiante
                                    </h4>
                                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Apellidos *</label>
                                            <input v-model="newStudentForm.apellidos" type="text" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                            <span v-if="newStudentForm.errors.apellidos" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.apellidos }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Nombres *</label>
                                            <input v-model="newStudentForm.nombres" type="text" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                            <span v-if="newStudentForm.errors.nombres" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.nombres }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Tipo de Documento *</label>
                                            <select v-model="newStudentForm.id_type_document" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all">
                                                <option value="" disabled>Seleccionar...</option>
                                                <option v-for="type in typeDocuments" :key="type.id" :value="type.id">{{ type.label }}</option>
                                            </select>
                                            <span v-if="newStudentForm.errors.id_type_document" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.id_type_document }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Número de Documento *</label>
                                            <input v-model="newStudentForm.num_doc" type="text" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                            <span v-if="newStudentForm.errors.num_doc" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.num_doc }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Sexo *</label>
                                            <select v-model="newStudentForm.sexo" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all">
                                                <option value="" disabled>Seleccionar...</option>
                                                <option value="0">Femenino</option>
                                                <option value="1">Masculino</option>
                                            </select>
                                            <span v-if="newStudentForm.errors.sexo" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.sexo }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Fecha Nacimiento *</label>
                                            <input v-model="newStudentForm.fecha_nacimiento" type="date" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                            <span v-if="newStudentForm.errors.fecha_nacimiento" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.fecha_nacimiento }}</span>
                                        </div>
                                        <div class="lg:col-span-2">
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Domicilio</label>
                                            <input v-model="newStudentForm.domicilio" type="text" class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                            <span v-if="newStudentForm.errors.domicilio" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.domicilio }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Celular de Contacto</label>
                                            <input v-model="newStudentForm.celular" type="text" class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                            <span v-if="newStudentForm.errors.celular" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.celular }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Nivel Educativo *</label>
                                            <select v-model="newStudentForm.id_level" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all">
                                                <option value="" disabled>Seleccionar...</option>
                                                <option v-for="level in levels" :key="level.id" :value="level.id">{{ level.label }}</option>
                                            </select>
                                            <span v-if="newStudentForm.errors.id_level" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.id_level }}</span>
                                        </div>
                                        <div class="lg:col-span-2">
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Sede de Procedencia</label>
                                            <!-- <input v-model="newStudentForm.sede_origen" type="text" class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" /> -->
                                            <select v-model="newStudentForm.sede_origen" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all">
                                                <option value="" disabled>Seleccionar...</option>
                                                <option v-for="sede in sedes" :key="sede.id" :value="sede.id">{{ sede.label }}</option>
                                            </select>
                                            <span v-if="newStudentForm.errors.sede_origen" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.sede_origen }}</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Sección Apoderado -->
                                <div>
                                    <h4 class="text-sm font-bold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2 border-b border-slate-200 dark:border-slate-700 pb-2">
                                        <Icon icon="tabler:users-group" class="w-4 h-4 text-indigo-500" />
                                        Datos del Apoderado/Contacto (Responsable de pago)
                                    </h4>
                                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Tipo de Documento *</label>
                                            <select v-model="newStudentForm.contact_id_type_document" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all">
                                                <option value="" disabled>Seleccionar...</option>
                                                <option v-for="type in typeDocuments" :key="type.id" :value="type.id">{{ type.label }}</option>
                                            </select>
                                            <span v-if="newStudentForm.errors.contact_id_type_document" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.contact_id_type_document }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Número de Documento *</label>
                                            <div class="relative">
                                                <input v-model="newStudentForm.contact_num_doc" @blur="searchContact" @keyup.enter.prevent="searchContact" type="text" required class="w-full px-3 py-2 pr-10 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                                <div v-if="isSearchingContact" class="absolute right-3 top-1/2 -translate-y-1/2">
                                                    <Icon icon="tabler:loader" class="w-4 h-4 text-indigo-500 animate-spin" />
                                                </div>
                                                <button v-else @click.prevent="searchContact" type="button" class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-500 transition-colors" title="Buscar Apoderado">
                                                    <Icon icon="tabler:search" class="w-4 h-4" />
                                                </button>
                                            </div>
                                            <span v-if="newStudentForm.errors.contact_num_doc" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.contact_num_doc }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Parentesco *</label>
                                            <select v-model="newStudentForm.contact_id_parentesco" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all">
                                                <option value="" disabled>Seleccionar...</option>
                                                <option v-for="parentesco in parentescos" :key="parentesco.id" :value="parentesco.id">{{ parentesco.label }}</option>
                                            </select>
                                            <span v-if="newStudentForm.errors.contact_id_parentesco" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.contact_id_parentesco }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Apellidos *</label>
                                            <input v-model="newStudentForm.contact_apellidos" type="text" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                            <span v-if="newStudentForm.errors.contact_apellidos" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.contact_apellidos }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Nombres *</label>
                                            <input v-model="newStudentForm.contact_nombres" type="text" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                            <span v-if="newStudentForm.errors.contact_nombres" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.contact_nombres }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Sexo *</label>
                                            <select v-model="newStudentForm.contact_sexo" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all">
                                                <option value="" disabled>Seleccionar...</option>
                                                <option value="0">Femenino</option>
                                                <option value="1">Masculino</option>
                                            </select>
                                            <span v-if="newStudentForm.errors.contact_sexo" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.contact_sexo }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Celular *</label>
                                            <input v-model="newStudentForm.contact_celular" type="text" required class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                            <span v-if="newStudentForm.errors.contact_celular" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.contact_celular }}</span>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Email *</label>
                                            <input v-model="newStudentForm.contact_email" type="email" class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:text-white transition-all" />
                                            <span v-if="newStudentForm.errors.contact_email" class="text-[10px] text-rose-500 mt-1 block">{{ newStudentForm.errors.contact_email }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Footer Actions -->
                            <div class="bg-slate-50 dark:bg-slate-900/40 px-6 py-4 border-t border-slate-100 dark:border-slate-700/80 flex items-center justify-end gap-3 rounded-b-2xl mt-auto">
                                <button type="button" 
                                        @click="closeModal" 
                                        class="px-4 py-2 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                                    Cancelar
                                </button>
                                <button type="submit" 
                                        :disabled="newStudentForm.processing"
                                        class="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow transition-all flex items-center gap-2">
                                    <Icon v-if="newStudentForm.processing" icon="tabler:loader" class="w-4 h-4 animate-spin" />
                                    Registrar Estudiante
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

        </div>
    </BreezeAuthenticatedLayout>
</template>
